import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { updatePageMeta } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';
import { BLOG_CATEGORIES } from '../data/blogConfig';
import './Blog.css';

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Blog() {
  // Post pages link to /blog?category=… and /blog?tag=… (2026-10-16: honoured).
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    tag: searchParams.get('tag') || '',
    search: '',
    page: 1,
    limit: 9
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0
  });

  const categoryNames = BLOG_CATEGORIES;

  useEffect(() => {
    updatePageMeta(metaFor('/blog'));
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [filters]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v != null));
      const response = await axios.get(`${API_URL}/blog/published`, { params });
      
      // Handle API response - backend returns {success, data: [...]}
      const postsData = response.data.data || response.data.posts || [];
      setPosts(Array.isArray(postsData) ? postsData : []);
      
      const paginationData = response.data.pagination || {};
      setPagination({
        currentPage: paginationData.page || 1,
        totalPages: paginationData.pages || 1,
        total: paginationData.total || 0
      });
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/blog/categories/list`);
      const categoriesData = response.data.data || response.data;
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]); // Set empty array on error
    }
  };

  const handleCategoryChange = (category) => {
    setFilters(prev => ({ ...prev, category, tag: '', page: 1 }));
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo(0, 0);
  };

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <h1>Photography Blog</h1>
          <p className="lead">Tips, insights, and stories from Utah's premier real estate photographers</p>
        </div>
      </section>

      <div className="blog-content">
        <div className="container">
          {/* Horizontal Filter Bar */}
          <div className="blog-filter-bar">
            <div className="blog-search-inline">
              <input
                type="text"
                placeholder="Search articles..."
                value={filters.search}
                onChange={handleSearchChange}
              />
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:'1.2rem',height:'1.2rem',position:'absolute',right:'12px',top:'50%',transform:'translateY(-50%)',color:'#9ca3af'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="blog-category-tabs">
              <button
                onClick={() => handleCategoryChange('')}
                className={!filters.category ? 'active' : ''}
              >
                All Posts ({pagination.total})
              </button>
              {categories.map(cat => (
                <button
                  key={cat._id}
                  onClick={() => handleCategoryChange(cat._id)}
                  className={filters.category === cat._id ? 'active' : ''}
                >
                  {categoryNames[cat._id] || cat._id} ({cat.count})
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="empty-state">
              <h2>No posts found</h2>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              {/* Posts Grid */}
              <div className="posts-grid">
                {posts.map(post => (
                  <article key={post._id} className="post-card">
                    {post.featuredImage && (
                      <Link to={`/blog/${post.slug}`} className="post-image">
                        <img 
                          src={post.featuredImage.url} 
                          alt={post.featuredImage.alt || post.title}
                          loading="lazy"
                        />
                      </Link>
                    )}
                    
                    <div className="post-content">
                      <div className="post-meta">
                        <span className="category">{categoryNames[post.category]}</span>
                        <span className="separator">•</span>
                        <span className="read-time">{post.readTime} min read</span>
                      </div>
                      
                      <h2 className="post-title">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h2>
                      
                      <p className="post-excerpt">{post.excerpt}</p>
                      
                      <div className="post-footer">
                        <div className="post-author">
                          <span>By {post.authorName}</span>
                        </div>
                        <Link to={`/blog/${post.slug}`} className="read-more">
                          Read More →
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="pagination-btn"
                  >
                    ← Previous
                  </button>
                  
                  <div className="pagination-numbers">
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`pagination-number ${page === pagination.currentPage ? 'active' : ''}`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="pagination-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}

          {/* CTA */}
          <div className="blog-bottom-cta">
            <h3>Ready to Book?</h3>
            <p>Get professional photos for your listings</p>
            <Link to="/book" className="btn btn-primary">
              Book Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Blog;
